import React, { useState, useMemo } from "react";
import {
  LocationIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronDownIcon,
  InfoIcon,
} from "../icons";
import CustomSelect from "../ui/CustomSelect";
import {
  formatCurrency,
  formatConcatenatedAddress,
  formatBandwidth,
  formatPhoneNumber,
  formatCircuitIdForDisplay ,
} from "../../utils/helpers";
import { INVENTORY_FIELD_LABELS, GROUPED_TABLE_COLUMNS } from "../../config/inventoryFields";
import { getInventoryColumnLabel } from "../../utils/columnLabelUtils";
import { formatDateYmdStable } from "../../utils/dateUtils";
import { canMutateTenantData } from "../../config/constants";
import {
  DISCONNECT_STATUS,
  statusLabel,
} from "../../services/disconnectService";

/**
 * Disconnect status under Circuit ID on Detailed Inventory.
 * Submitted → Pending disconnect (peach). Confirmed + still billing → Still billing (pink).
 */
function DisconnectStatusBadge({ status, billingCheck }) {
  if (!status) return null;

  const stillBilling =
    billingCheck === "still_billing" &&
    (status === DISCONNECT_STATUS.CONFIRMED ||
      status === DISCONNECT_STATUS.SUBMITTED);

  let label;
  let cls;
  if (stillBilling) {
    label = "Still billing";
    cls = "bg-[#fde8e8] text-[#a51c1c] border-[#f5c2c2]";
  } else if (status === DISCONNECT_STATUS.SUBMITTED) {
    label = "Pending disconnect";
    cls = "bg-[#fdeee0] text-[#b45309] border-[#f5d0a9]";
  } else if (status === DISCONNECT_STATUS.CONFIRMED) {
    label = statusLabel(status);
    cls = "bg-amber-100 text-amber-900 border-amber-200";
  } else if (status === DISCONNECT_STATUS.BILLING_STOPPED) {
    label = statusLabel(status);
    cls = "bg-emerald-100 text-emerald-900 border-emerald-200";
  } else {
    label = statusLabel(status);
    cls = "bg-sky-100 text-sky-900 border-sky-200";
  }

  return (
    <span
      className={`inline-flex w-fit max-w-full flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-tight ${cls}`}
      title={label}
    >
      {label}
    </span>
  );
}

/** Red power icon + hover tooltip for closed sites (design reference). */
const ClosedSiteStatusIcon = () => (
  <span
    className="relative inline-flex flex-shrink-0 group/closed-tip"
    onClick={(e) => e.stopPropagation()}
  >
    <span className="inline-flex text-red-600" aria-hidden="true">
      <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
        <path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.79 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.21 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z" />
      </svg>
    </span>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-red-600 px-3 py-1.5 text-[13px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover/closed-tip:opacity-100"
    >
      The site is closed
      <span
        className="absolute left-1/2 top-full -translate-x-1/2 border-[6px] border-transparent border-t-red-600"
        aria-hidden="true"
      />
    </span>
    <span className="sr-only">The site is closed</span>
  </span>
);

const GroupedInventoryTable = ({
  inventory,
  userRole,
  tenant,
  tenantId,
  onEditItem,
  onDeleteItem,
  onViewInvoices,
  onOpenSiteDetails,
  currentPage,
  totalPages,
  totalRecords,
  totalGroups,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortConfig,
  onSortChange,
  providers = [],
  categories = [],
  columnMapping = null,
  fieldMappingLookup = {},
}) => {

  // Use only dynamic values from props (no fallback)
  const sortedProviders = [...providers].sort();
  const sortedCategories = [...categories].sort();

  // State management for address expansion
  const [expandedAddresses, setExpandedAddresses] = useState(new Set());

  // Active category tab per expanded address (Data / Voice / Analog style)
  const [activeCategoryTab, setActiveCategoryTab] = useState({});

  // ==================== UTILITY FUNCTIONS ====================

  // Normalize category and provider using dynamic values
  const normalizeCategory = (category) => {
    if (!category) return "Other";
    const categoryStr = String(category);
    const match = sortedCategories.find(
      (c) => c && String(c).toLowerCase() === categoryStr.toLowerCase()
    );
    return match || category;
  };

  const normalizeProvider = (provider) => {
    if (!provider) return "Unknown";
    const providerStr = String(provider);
    const match = sortedProviders.find(
      (p) => p && String(p).toLowerCase() === providerStr.toLowerCase()
    );
    return match || provider;
  };

  // Group items by category with totals (alphabetically sorted)
  const groupByCategory = (items) => {
    if (!items || items.length === 0) return [];

    const categoryMap = new Map();

    items.forEach((item) => {
      const category = normalizeCategory(item.category);

      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          category,
          items: [],
          totalCount: 0,
          totalMRC: 0,
        });
      }

      const categoryGroup = categoryMap.get(category);
      categoryGroup.items.push(item);
      categoryGroup.totalCount += 1;
      categoryGroup.totalMRC += parseFloat(item.mrc) || 0;
    });

    // After grouping, sort items within each category by provider
    Array.from(categoryMap.values()).forEach((categoryGroup) => {
      categoryGroup.items.sort((a, b) => {
        const providerA = normalizeProvider(a.provider) || "ZZZ"; // Place null/undefined at end
        const providerB = normalizeProvider(b.provider) || "ZZZ";
        return providerA.localeCompare(providerB, undefined, {
          sensitivity: "base",
        });
      });
    });

    // Sort alphabetically: Analog, Data, Mobility, etc.
    return Array.from(categoryMap.values()).sort((a, b) =>
      a.category.localeCompare(b.category)
    );
  };

  // Process inventory data with category grouping
  const groupedInventory = useMemo(() => {
    if (!inventory || inventory.length === 0) return [];

    return inventory.map((group) => {
      const items = group.items || [];
      const categoryGroups = groupByCategory(items);

      // Calculate total services count and total MRC for this address
      // Use backend values if available, otherwise calculate from items
      let totalCount = group.totalCount;
      let totalMRC = group.totalMRC;

      // If backend didn't provide totals, calculate them from items
      if (totalCount === undefined || totalCount === null) {
        totalCount = items.length;
      }

      if (totalMRC === undefined || totalMRC === null || totalMRC === 0) {
        // Calculate total MRC from all items
        totalMRC = items.reduce((sum, item) => {
          // Try multiple ways to get MRC value
          let mrc = item.mrc || item.MRC || null;
          if (
            !mrc &&
            fieldMappingLookup &&
            Object.keys(fieldMappingLookup).length > 0
          ) {
            const mrcInputColumns = Object.entries(fieldMappingLookup)
              .filter(([inputCol, dbCol]) => dbCol === "mrc")
              .map(([inputCol]) => inputCol);
            for (const inputCol of mrcInputColumns) {
              if (
                item[inputCol] !== undefined &&
                item[inputCol] !== null &&
                item[inputCol] !== ""
              ) {
                mrc = item[inputCol];
                break;
              }
            }
          }
          if (!mrc) {
            mrc =
              item["Total $"] || item["Total$"] || item["USD Amount"] || null;
          }
          if (!mrc) {
            const rowKeys = Object.keys(item);
            for (const key of rowKeys) {
              const keyLower = key.toLowerCase().trim();
              if (
                keyLower === "mrc" ||
                keyLower === "total $" ||
                keyLower === "total$" ||
                keyLower === "usd amount"
              ) {
                mrc = item[key];
                break;
              }
            }
          }
          return sum + (parseFloat(mrc) || 0);
        }, 0);
      } else {
        // Ensure totalMRC is a number
        totalMRC = parseFloat(totalMRC) || 0;
      }

      return {
        ...group,
        address:
          group.address ||
          formatConcatenatedAddress({
            std_street_address: group.std_street_address,
            std_city: group.std_city,
            std_state: group.std_state,
            std_zip: group.std_zip,
            std_country: group.std_country,
          }),
        categoryGroups: categoryGroups,
        items: items,
        totalCount: totalCount,
        totalMRC: totalMRC,
      };
    });
  }, [inventory, fieldMappingLookup]);

  // ==================== STATE MANAGEMENT ====================

  const toggleAddress = (addressKey, categoryGroups = []) => {
    setExpandedAddresses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(addressKey)) {
        newSet.delete(addressKey);
      } else {
        newSet.add(addressKey);
        const firstCategory = categoryGroups[0]?.category;
        if (firstCategory) {
          setActiveCategoryTab((tabs) =>
            tabs[addressKey] ? tabs : { ...tabs, [addressKey]: firstCategory }
          );
        }
      }
      return newSet;
    });
  };

  const isAddressExpanded = (addressKey) => expandedAddresses.has(addressKey);

  const setCategoryTab = (addressKey, categoryName) => {
    setActiveCategoryTab((tabs) => ({ ...tabs, [addressKey]: categoryName }));
  };

  const getActiveCategoryGroup = (addressKey, categoryGroups) => {
    if (!categoryGroups?.length) return null;
    const activeName = activeCategoryTab[addressKey];
    return (
      categoryGroups.find((g) => g.category === activeName) || categoryGroups[0]
    );
  };

  // ==================== FIELD VALUE HELPERS ====================

  // Helper function to get field value from item using column name and mapping
  const getFieldValue = (item, columnName) => {
    if (!columnName) return null;

    // Handle placeholder fields (not yet in backend)
    if (columnName === "taxes_and_surcharges" || columnName === "total") {
      return null;
    }

    // Direct access
    if (
      item[columnName] !== undefined &&
      item[columnName] !== null &&
      item[columnName] !== ""
    ) {
      return item[columnName];
    }

    // Try field mapping lookup
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      const dbColumn = fieldMappingLookup[columnName];
      if (
        dbColumn &&
        item[dbColumn] !== undefined &&
        item[dbColumn] !== null &&
        item[dbColumn] !== ""
      ) {
        return item[dbColumn];
      }
    }

    // Try common variations for known fields
    const lowerCol = columnName.toLowerCase();
    if (
      lowerCol.includes("circuit") ||
      lowerCol.includes("nit") ||
      lowerCol.includes("service number")
    ) {
      return (
        item.circuit_id ||
        item.NIT ||
        item["Circuit ID"] ||
        item["Service Number"] ||
        null
      );
    }
    if (lowerCol.includes("bandwidth")) {
      return (
        item.bandwidth ||
        item["Bandwidth"] ||
        item["Bandwidth Capacity"] ||
        null
      );
    }
    if (
      lowerCol === "mrc" ||
      lowerCol.includes("total") ||
      lowerCol.includes("usd")
    ) {
      return (
        item.mrc || item.MRC || item["Total $"] || item["USD Amount"] || null
      );
    }
    if (lowerCol.includes("term") && !lowerCol.includes("end")) {
      return (
        item.contract_term ||
        item["Term"] ||
        item["Contract Term (months)"] ||
        null
      );
    }
    if (lowerCol.includes("end") && lowerCol.includes("term")) {
      return (
        item.end_of_term || item["End of Term"] || item["Expiration"] || null
      );
    }

    return null;
  };

  // Helper function to identify MRC columns
  const isMRCColumn = (columnName) => {
    if (!columnName) return false;
    if (columnName.toLowerCase() === "mrc") return true;
    const knownMRCColumns = ["MRC", "Total $", "USD Amount"];
    if (knownMRCColumns.includes(columnName)) return true;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      const mappedColumn = fieldMappingLookup[columnName];
      if (mappedColumn && mappedColumn.toLowerCase() === "mrc") return true;
    }
    return false;
  };

  // Helper function to format field value for display
  const formatFieldValue = (value, columnName, itemCategory) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const lowerCol = columnName?.toLowerCase() || "";

    // Format currency for MRC
    if (isMRCColumn(columnName)) {
      return formatCurrency(value);
    }

    // Format dates (timezone-stable YYYY-MM-DD for all viewers)
    if (
      lowerCol.includes("date") ||
      (lowerCol.includes("term") && lowerCol.includes("end"))
    ) {
      const ymd = formatDateYmdStable(value);
      if (ymd) return ymd;
    }

    // Format bandwidth
    if (lowerCol.includes("bandwidth")) {
      return formatBandwidth(value);
    }

    // Format phone numbers for circuit_id in Mobility/Analog/Voice categories
    if (
      (lowerCol.includes("circuit") ||
        lowerCol.includes("nit") ||
        lowerCol.includes("service number")) &&
      (itemCategory === "Mobility" ||
        itemCategory === "Analog" ||
        itemCategory === "Voice")
    ) {
      return formatCircuitIdForDisplay (value);
    }

    // Return as string
    return String(value);
  };

  // Get ordered columns for display using static configuration
  const orderedColumns = useMemo(() => {
    // Use static column configuration (filter out provider, category, and mrc as they're handled separately)
    return GROUPED_TABLE_COLUMNS.filter((col) => {
      const colLower = col.toLowerCase();
      return (
        colLower !== "provider" &&
        colLower !== "category" &&
        colLower !== "mrc"
      );
    });
  }, []);

  // Check if Provider column should be shown
  const hasProviderData = useMemo(() => {
    if (!inventory || inventory.length === 0) return false;
    return inventory.some((group) =>
      (group.items || []).some(
        (item) =>
          item.provider && item.provider !== null && item.provider !== ""
      )
    );
  }, [inventory]);

  // ==================== FORMATTING FUNCTIONS ====================

  // Get status color class
  const getStatusColor = (status) => {
    if (!status) return "text-gray-500";
    const statusLower = String(status).toLowerCase().trim();

    if (
      statusLower === "pending" ||
      statusLower === "pending install" ||
      statusLower === "pending disconnect" ||
      statusLower.startsWith("pending")
    ) {
      return "text-gray-500";
    }

    if (statusLower === "in service" || statusLower === "active") {
      return "text-green-600";
    }

    if (
      statusLower === "canceled" ||
      statusLower === "cancelled" ||
      statusLower.includes("cancel")
    ) {
      return "text-red-600";
    }

    if (
      statusLower === "disconnected" ||
      (statusLower.includes("disconnect") && !statusLower.includes("pending"))
    ) {
      return "text-red-600";
    }

    if (
      statusLower === "inactive" ||
      statusLower === "in active" ||
      statusLower.includes("inactive") ||
      statusLower === "suspended" ||
      statusLower.includes("suspend")
    ) {
      return "text-gray-500";
    }

    return "text-gray-500";
  };

  // ==================== SORTING ====================

  const handleSortClick = (e, columnName, direction) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      if (
        sortConfig?.sortBy === columnName &&
        sortConfig?.sortOrder === direction
      ) {
        onSortChange(null, null);
      } else {
        onSortChange(columnName, direction);
      }
    } catch (error) {
      console.error("âŒ Error sorting addresses:", error);
      alert("Failed to sort addresses. Please try again.");
    }
  };

  const renderSortButtons = () => {
    const isAscActive =
      sortConfig?.sortBy === "address" && sortConfig?.sortOrder === "asc";
    const isDescActive =
      sortConfig?.sortBy === "address" && sortConfig?.sortOrder === "desc";

    const activeColorClass = "text-[var(--accent,#a51c1c)]";

    return (
      <div className="inline-flex flex-col ml-1 gap-0">
        <button
          onClick={(e) => handleSortClick(e, "address", "asc")}
          className={`p-0.5 rounded transition-colors cursor-pointer ${
            isAscActive
              ? `${activeColorClass}`
              : "text-[var(--faint,#9a9a95)] hover:text-[var(--ink,#1c1c1a)]"
          }`}
          title="Sort addresses ascending"
          aria-label="Sort address ascending"
          type="button"
        >
          <ArrowUpIcon className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => handleSortClick(e, "address", "desc")}
          className={`p-0.5 rounded transition-colors cursor-pointer ${
            isDescActive
              ? `${activeColorClass}`
              : "text-[var(--faint,#9a9a95)] hover:text-[var(--ink,#1c1c1a)]"
          }`}
          title="Sort addresses descending"
          aria-label="Sort address descending"
          type="button"
        >
          <ArrowDownIcon className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const getDataColumnCount = (category) => {
    const filteredColumns = (orderedColumns || []).filter(
      (col) => col.toLowerCase() !== "address"
    );
    let count = filteredColumns.length + 1; // MRC
    if (hasProviderData) count += 1;
    if (category === "Data") {
      const hasServiceType = filteredColumns.some(
        (col) => col.toLowerCase().replace(/[^a-z]/g, "") === "servicetype"
      );
      if (hasServiceType) count += 1;
    }
    return Math.max(count, 1);
  };

  const renderEqualColGroup = (category) => {
    const dataCount = getDataColumnCount(category);
    return (
      <colgroup>
        {Array.from({ length: dataCount }, (_, i) => (
          <col key={`data-${i}`} />
        ))}
        <col key="action" style={{ width: "8.75rem" }} />
      </colgroup>
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  // Render table header row for expanded services (same as ImportPage)
  const renderTableHeader = (category) => {
    if (!orderedColumns || orderedColumns.length === 0) {
      return null;
    }

    // Filter out "address" column since it's already shown in the group header
    const filteredColumns = orderedColumns.filter(
      (col) => col.toLowerCase() !== "address"
    );

    // Show Provider column if data exists
    const showProviderColumn = hasProviderData;

    // Last Mile Provider column is only shown for the Data category,
    const showLastMileColumn = category === "Data";

    return (
      <thead>
        <tr>
          {/* Provider column */}
          {showProviderColumn && (
            <th scope="col" className="text-[11px] font-semibold uppercase tracking-[0.05em] px-3 py-2 text-left whitespace-nowrap align-middle text-[var(--muted,#6b6a66)]">
              Provider
            </th>
          )}
          {/* Dynamic columns */}
          {filteredColumns.map((col) => {
            const isServiceTypeColumn = col.toLowerCase().replace(/[^a-z]/g, "") === "servicetype";
            return (
              <React.Fragment key={col}>
                <th
                  scope="col"
                  className="text-[11px] font-semibold uppercase tracking-[0.05em] px-3 py-2 text-left whitespace-nowrap align-middle text-[var(--muted,#6b6a66)]"
                >
                  {getInventoryColumnLabel(
                    col,
                    fieldMappingLookup,
                    INVENTORY_FIELD_LABELS
                  )}
                </th>
                {showLastMileColumn && isServiceTypeColumn && (
                  <th scope="col" className="text-[11px] font-semibold uppercase tracking-[0.05em] px-3 py-2 text-left whitespace-nowrap align-middle text-[var(--muted,#6b6a66)]">
                    Last mile
                  </th>
                )}
              </React.Fragment>
            );
          })}
          <th scope="col" className="text-[11px] font-semibold uppercase tracking-[0.05em] px-3 py-2 text-left whitespace-nowrap align-middle text-[var(--muted,#6b6a66)]">
            MRC
          </th>
          <th scope="col" className="text-[11px] font-semibold uppercase tracking-[0.05em] px-2 py-2 text-center whitespace-nowrap align-middle text-[var(--muted,#6b6a66)]">
            Action
          </th>
        </tr>
      </thead>
    );
  };

  // Render a single service item (same structure as ImportPage renderItemRow)
  const renderServiceItem = (item, index) => {
    const uniqueKey = item.id || item.circuit_id || `item_${index}`;

    // Get MRC - comprehensive extraction
    let mrc = null;
    if (fieldMappingLookup && Object.keys(fieldMappingLookup).length > 0) {
      const mrcInputColumns = Object.entries(fieldMappingLookup)
        .filter(([inputCol, dbCol]) => dbCol === "mrc")
        .map(([inputCol]) => inputCol);
      for (const inputCol of mrcInputColumns) {
        if (
          item[inputCol] !== undefined &&
          item[inputCol] !== null &&
          item[inputCol] !== ""
        ) {
          mrc = item[inputCol];
          break;
        }
      }
    }
    if (!mrc) {
      mrc = item.mrc || item.MRC || null;
    }
    if (!mrc) {
      mrc = item["Total $"] || item["Total$"] || item["USD Amount"] || null;
    }
    if (!mrc) {
      const rowKeys = Object.keys(item);
      for (const key of rowKeys) {
        const keyLower = key.toLowerCase().trim();
        if (
          keyLower === "mrc" ||
          keyLower === "total $" ||
          keyLower === "total$" ||
          keyLower === "usd amount"
        ) {
          mrc = item[key];
          break;
        }
      }
    }

    const itemCategory = normalizeCategory(item.category) || "-";
    const itemProvider = normalizeProvider(item.provider) || "-";

    const showProviderColumn = hasProviderData;

    return (
      <tr
        key={uniqueKey}
        className="hover:bg-[var(--sunken,#eeece7)] transition-colors"
      >
        {/* Provider column */}
        {showProviderColumn && (
          <td className="px-3 py-2.5 text-[13px] align-middle text-[var(--ink,#1c1c1a)] truncate max-w-0">{itemProvider}</td>
        )}

        {/* Dynamic columns */}
        {orderedColumns && orderedColumns.length > 0 ? (
          (() => {
            const filteredColumns = orderedColumns.filter(
              (col) => col.toLowerCase() !== "address"
            );
            return filteredColumns.map((col) => {
              const value = getFieldValue(item, col);
              const mappedField = fieldMappingLookup?.[col];
              const isCircuitIdColumn =
                col.toLowerCase().includes("circuit") ||
                col.toLowerCase().includes("nit") ||
                col.toLowerCase().includes("service number") ||
                mappedField === "circuit_id";
              const isPrimaryCircuitIdColumn =
                mappedField === "circuit_id" ||
                col === "circuit_id" ||
                col.toLowerCase() === "circuit id";
              const shouldFormatAsPhone =
                isCircuitIdColumn &&
                (itemCategory === "Mobility" ||
                  itemCategory === "Analog" ||
                  itemCategory === "Voice");
              const formattedValue = shouldFormatAsPhone
                ? formatCircuitIdForDisplay (value)
                : formatFieldValue(value, col, itemCategory);
              const isServiceTypeColumn =
                col.toLowerCase().replace(/[^a-z]/g, "") === "servicetype";
              const isFigureCol =
                isCircuitIdColumn ||
                mappedField === "billing_account" ||
                mappedField === "mrc" ||
                mappedField === "end_of_term" ||
                mappedField === "follow_up_date" ||
                col.toLowerCase().includes("account") ||
                col.toLowerCase().includes("date") ||
                col.toLowerCase().includes("mrc");
              return (
                <React.Fragment key={col}>
                  <td
                    className={`px-3 py-2.5 text-[13px] align-middle text-[var(--ink,#1c1c1a)] max-w-0${
                      isFigureCol ? " tabular-nums" : ""
                    }${isPrimaryCircuitIdColumn ? "" : " truncate"}`}
                  >
                    {isPrimaryCircuitIdColumn ? (
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate">{formattedValue}</span>
                        <DisconnectStatusBadge
                          status={item.disconnect_status}
                          billingCheck={item.disconnect_billing_check}
                        />
                      </div>
                    ) : (
                      formattedValue
                    )}
                  </td>
                  {itemCategory === "Data" && isServiceTypeColumn && (
                    <td className="px-3 py-2.5 text-[13px] align-middle text-[var(--ink,#1c1c1a)] truncate max-w-0">
                      {item.last_mile_provider ? item.last_mile_provider : "-"}
                    </td>
                  )}
                </React.Fragment>
              );
            });
          })()
        ) : (
          <td
            colSpan={orderedColumns?.length || 1}
            className="px-3 py-2.5 text-[14px] text-gray-500 align-middle border-b border-gray-200"
          >
            No columns available
          </td>
        )}

        {/* MRC Column (Right) */}
        <td className="px-3 py-2.5 text-[13px] align-middle text-[var(--ink,#1c1c1a)] truncate max-w-0 tabular-nums">
          {mrc ? formatCurrency(mrc) : "-"}
        </td>

        <td className="px-2 py-2.5 text-right align-middle whitespace-nowrap">
          <div className="inline-flex items-center justify-end gap-0.5">
            {(() => {
              const actionText = (getFieldValue(item, "action") ?? item.action ?? "")
                .toString()
                .trim();
              if (!actionText) return null;
              return (
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center text-red-600 hover:text-red-700 transition-colors cursor-help"
                  title={actionText}
                  aria-label={`Follow up: ${actionText}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-7.6z" />
                  </svg>
                </button>
              );
            })()}
            {onEditItem && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditItem({ current: item, original: null });
                }}
                className="inline-flex h-7 w-7 items-center justify-center text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)] transition-colors"
                title={canMutateTenantData(userRole) ? "Edit" : "View"}
                aria-label={canMutateTenantData(userRole) ? "Edit" : "View"}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
            {onViewInvoices && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const account =
                    getFieldValue(item, "billing_account") ??
                    item.billing_account ??
                    "";
                  onViewInvoices(account);
                }}
                disabled={
                  !(
                    getFieldValue(item, "billing_account") ||
                    item.billing_account
                  )
                }
                className="inline-flex h-7 w-7 items-center justify-center text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="View invoices for this account"
                aria-label="View invoices for this account"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </button>
            )}
            {onDeleteItem && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteItem({ current: item, original: null });
                }}
                className="inline-flex h-7 w-7 items-center justify-center text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)] transition-colors"
                title="Delete"
                aria-label="Delete"
              >
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Render expanded address panel: category tabs + service table (design reference)
  const renderExpandedAddressPanel = (group) => {
    const { addressKey, categoryGroups } = group;
    if (!categoryGroups?.length) return null;

    const activeGroup = getActiveCategoryGroup(addressKey, categoryGroups);
    const activeTab = activeGroup?.category;

    return (
      <div className="border-t border-[var(--line,#e4e2dc)] bg-white">
        <div
          className="flex items-center gap-0 px-4 pt-1"
          onClick={(e) => e.stopPropagation()}
        >
          {categoryGroups.map((categoryGroup) => {
            const isActive = categoryGroup.category === activeTab;
            return (
              <button
                key={categoryGroup.category}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoryTab(addressKey, categoryGroup.category);
                }}
                className={`px-3 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                  isActive
                    ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)]"
                    : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
                }`}
              >
                {categoryGroup.category}
                <span
                  className={`ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] ${
                    isActive
                      ? "bg-[var(--accent-soft,#f3e8e8)] text-[var(--accent,#a51c1c)]"
                      : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                  }`}
                >
                  {categoryGroup.totalCount}
                </span>
              </button>
            );
          })}
        </div>
        {activeGroup && (
          <div className="overflow-x-auto px-4 py-2 pb-3">
            <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] overflow-hidden">
              <table className="w-full mb-0 table-fixed">
                {renderEqualColGroup(activeGroup.category)}
                {renderTableHeader(activeGroup.category)}
                <tbody>
                  {activeGroup.items.map((item, idx) =>
                    renderServiceItem(item, idx)
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render main address row
  const renderAddressRow = (group, rowIndex) => {
    const addressExpanded = isAddressExpanded(group.addressKey);
    const baseAddress = group.address || "-";
    const siteName =
      group.site_name != null ? String(group.site_name).trim() : "";
    const isClosed =
      String(group.site_status || "").trim().toLowerCase() === "closed";
    const serviceCount =
      group.totalCount || (group.items || []).length;
    const servicesLabel = `${serviceCount} ${
      serviceCount === 1 ? "service" : "services"
    }`;
    const amountText = formatCurrency(group.totalMRC || 0);
    const rowTextClass = isClosed ? "text-red-700" : "text-[var(--ink,#1c1c1a)]";
    const servicesTextClass = isClosed ? "text-red-700" : "text-[var(--muted,#6b6a66)]";
    const amountTextClass = isClosed
      ? "text-red-700 font-semibold"
      : "text-[var(--ink,#1c1c1a)] font-semibold";
    const rowBgClass = isClosed
      ? "bg-red-50 hover:bg-red-100/70"
      : "bg-white hover:bg-[var(--sunken,#eeece7)]";

    const openSiteDetails = (e) => {
      if (e) e.stopPropagation();
      if (!onOpenSiteDetails) return;
      onOpenSiteDetails({
        std_street_address: group.std_street_address,
        std_city: group.std_city,
        std_state: group.std_state,
        std_zip: group.std_zip,
        std_country: group.std_country,
        items: group.items || [],
      });
    };

    return (
      <div
        key={group.addressKey}
        className="rounded-[10px] border border-[var(--line,#e4e2dc)] overflow-hidden mb-2"
      >
        <div
          className={`cursor-pointer transition-colors ${rowBgClass}`}
          onClick={() =>
            toggleAddress(group.addressKey, group.categoryGroups || [])
          }
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <LocationIcon
                className={`w-[16px] h-[16px] flex-shrink-0 ${
                  isClosed ? "text-red-600" : "text-[var(--accent,#a51c1c)]"
                }`}
              />
              <span className={`text-[14px] font-semibold truncate ${rowTextClass}`}>
                {baseAddress}
              </span>
              {siteName ? (
                <span className="inline-flex items-center rounded-full bg-[var(--sunken,#eeece7)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink,#1c1c1a)] flex-shrink-0">
                  {siteName}
                </span>
              ) : null}
              {group.needs_master ? (
                <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-brand-red flex-shrink-0">
                  Needs master
                </span>
              ) : null}
              {isClosed && <ClosedSiteStatusIcon />}
            </div>
            <div
              className="w-[56px] flex-shrink-0 flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {onOpenSiteDetails ? (
                <button
                  type="button"
                  onClick={openSiteDetails}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)] transition-colors"
                  title="Site details"
                  aria-label="View site details"
                >
                  <InfoIcon className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            <div
              className={`w-[120px] text-right text-[13px] flex-shrink-0 ${servicesTextClass}`}
            >
              {servicesLabel}
            </div>
            <div
              className={`w-[140px] text-right text-[14px] whitespace-nowrap flex-shrink-0 tabular-nums ${amountTextClass}`}
            >
              {amountText}
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 flex-shrink-0 text-[var(--muted,#6b6a66)] transition-transform ${
                addressExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {addressExpanded &&
          (group.categoryGroups?.length > 0 ? (
            renderExpandedAddressPanel(group)
          ) : (
            <div className="px-5 py-3 text-center text-[13px] text-[var(--muted,#6b6a66)] border-t border-[var(--line,#e4e2dc)]">
              No services at this address
            </div>
          ))}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div>
      <div className="flex items-center gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        <div className="flex items-center min-w-0 flex-1">
          Address
          {renderSortButtons()}
        </div>
        <div className="w-[56px] text-center flex-shrink-0">Site</div>
        <div className="w-[120px] text-right flex-shrink-0">Services</div>
        <div className="w-[140px] text-right flex-shrink-0">Amount</div>
        <div className="w-4 flex-shrink-0" />
      </div>

      {groupedInventory.map((group, rowIndex) =>
        renderAddressRow(group, rowIndex)
      )}

      {groupedInventory.length === 0 && (
        <div className="text-center py-8 text-[var(--muted,#6b6a66)] text-[14px] rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white">
          No records found.
        </div>
      )}

      {/* Pagination Controls */}
      {(totalPages > 0 || (totalRecords !== undefined && totalRecords > 0)) && (
        <div className="py-3 px-1 mt-1">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <label className="text-[13px] text-[var(--muted,#6b6a66)]">Show:</label>
              <CustomSelect
                value={pageSize || 20}
                onChange={(e) =>
                  onPageSizeChange && onPageSizeChange(parseInt(e.target.value))
                }
                className="border border-[var(--line-strong,#d5d3cc)] rounded-md py-1.5 px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </CustomSelect>
              <span className="text-[13px] text-[var(--muted,#6b6a66)]">per page</span>
            </div>

            {/* Pagination Info and Controls */}
            {totalPages > 1 ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onPageChange(1)}
                  disabled={currentPage === 1}
                  title="First page"
                  aria-label="First page"
                  className="px-3 py-1.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ««
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                  aria-label="Previous page"
                  className="px-3 py-1.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    const isCurrentPage = currentPage === pageNum;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
                          isCurrentPage
                            ? "bg-[var(--accent,#a51c1c)] text-white"
                            : "bg-white text-[var(--ink,#1c1c1a)] border border-[var(--line-strong,#d5d3cc)] hover:bg-[var(--sunken,#eeece7)]"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                  aria-label="Next page"
                  className="px-3 py-1.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                  aria-label="Last page"
                  className="px-3 py-1.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  »»
                </button>
              </div>
            ) : (
              <div></div>
            )}

            {/* Page Info */}
            <div className="text-[13px] text-[var(--muted,#6b6a66)]">
              {totalPages > 0 && totalGroups !== undefined ? (
                <>
                  Showing{" "}
                  <span className="font-medium">
                    {((currentPage - 1) * pageSize + 1).toLocaleString()}
                  </span>
                  {" - "}
                  <span className="font-medium">
                    {Math.min(
                      currentPage * pageSize,
                      totalGroups
                    ).toLocaleString()}
                  </span>
                  {" of "}
                  <span className="font-medium">
                    {totalGroups.toLocaleString()}
                  </span>{" "}
                  {totalGroups === 1 ? "address" : "addresses"}
                  {totalRecords !== undefined && totalRecords > 0 && (
                    <span className="ml-1">
                      ({totalRecords.toLocaleString()} total records)
                    </span>
                  )}
                </>
              ) : totalPages > 0 ? (
                <>
                  Page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </>
              ) : (
                <span>No records</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedInventoryTable;
